import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, Play, Pause } from "lucide-react";
import { api, coverUrl, fmtDuration, queueAfter, packTrackToPlayable } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

export function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ tracks: [], packs: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const p = usePlayer();

  useEffect(() => { if (open) { setTimeout(() => inputRef.current?.focus(), 50); } else { setQ(""); setRes({ tracks: [], packs: [] }); } }, [open]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes({ tracks: [], packs: [] }); return; }
    setLoading(true);
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q.trim())}`).then((r) => {
      const needle = q.trim().toLowerCase();
      const pistas = (r.data.packs || []).flatMap((pack) => (pack.tracks || [])
        .filter((pt) => !pt.track_id && `${pt.title} ${pt.artist || ""} ${pt.genre || ""}`.toLowerCase().includes(needle))
        .map((pt) => packTrackToPlayable(pack, pt)));
      setRes({ tracks: [...r.data.tracks, ...pistas], packs: [] });
    }).finally(() => setLoading(false)), 250);
    return () => clearTimeout(t);
  }, [q]);

  if (!open) return null;
  const empty = q.trim().length >= 2 && !loading && res.tracks.length === 0 && res.packs.length === 0;

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-start justify-center pt-20 sm:pt-28 px-4" onClick={onClose} data-testid="search-overlay">
      <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 bg-[#111116] border border-white/10 rounded-2xl px-4 py-3 focus-within:border-red-600/60">
          <Search className="w-5 h-5 text-red-500 shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"
            placeholder="Buscar música: título, artista, género, tag…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 text-base" />
          <button type="button" onClick={onClose} data-testid="search-close" className="text-white/40 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        {(res.tracks.length > 0 || res.packs.length > 0 || empty || loading) && (
          <div className="mt-3 bg-[#111116] border border-white/10 rounded-2xl overflow-hidden max-h-[60vh] overflow-y-auto" data-testid="search-results">
            {loading && <div className="px-4 py-3 text-xs text-white/40 font-mono uppercase tracking-widest">Buscando…</div>}
            {empty && <div className="px-4 py-8 text-center text-white/40 text-sm" data-testid="search-empty">Sin resultados para "{q}"</div>}
            {res.tracks.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 font-mono text-[10px] text-red-500 uppercase tracking-[0.3em]">Temas · {res.tracks.length}</div>
                {res.tracks.map((t) => {
                  const isCur = p?.current?.id === t.id;
                  return (
                    <div key={t.id} data-testid={`search-track-${t.id}`} className={`flex items-center gap-3 px-4 py-2 hover:bg-white/[0.03] ${isCur ? "bg-red-950/20" : ""}`}>
                      <button type="button" onClick={() => (isCur ? p.togglePlay() : p.playTrack(t, queueAfter(res.tracks, t.id)))} data-testid={`search-play-${t.id}`}
                        className="relative w-10 h-10 rounded overflow-hidden bg-neutral-900 shrink-0 group">
                        {t.cover_path && <img src={coverUrl(t.cover_path, 160)} alt="" className="w-full h-full object-cover" />}
                        <span className="absolute inset-0 flex items-center justify-center bg-black/50">
                          {isCur && p?.isPlaying ? <Pause className="w-3.5 h-3.5 text-white" /> : <Play className="w-3.5 h-3.5 text-white ml-0.5" />}
                        </span>
                      </button>
                      <Link to={t.is_pista ? `/pack/${t.pack_id}` : `/track/${t.id}`} onClick={onClose} className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold truncate ${isCur ? "text-red-400" : "text-white"}`}>{t.title}</div>
                        <div className="text-xs text-white/50 truncate">{t.artist}{t.genre ? ` · ${t.genre}` : ""}{t.is_pista ? " · Pack" : ""}</div>
                      </Link>
                      <span className="font-mono text-[10px] text-white/40">{fmtDuration(t.duration)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
